/**
 * Server state, in one place.
 *
 * One deliberate choice: **nothing polls on a timer.** The queue is a list of
 * people we are about to telephone, and a table that reorders itself under the
 * cursor is how you cancel the wrong row. Every screen refetches when it is
 * focused again and after any action that could change it, and the queue page
 * shows a "N changes — refresh" affordance instead of moving on its own.
 */

import { useEffect } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  api,
  del,
  patch,
  post,
  put,
  type Account,
  type AccountConfig,
  type AccountWithQueue,
  type CallbackDetail,
  type CallbackListResponse,
  type CallingWindow,
  type ChannelRoute,
  type AgentFieldsResponse,
  type CallbackAgent,
  type CallbackAgentsResponse,
  type CodeActionInstallResponse,
  type CodeActionRecentCall,
  type CodeActionPlanResponse,
  type CodeActionScriptResponse,
  type CodeActionTestResponse,
  type EightloopAgentsResponse,
  type MeResponse,
  type OrchestratorChannelsResponse,
  type OrchestratorCredentialsResponse,
  type OrchestratorDetailResponse,
  type OrchestratorListResponse,
  type OrchestratorPreset,
  type OrchestratorRecordStatus,
  type OrchestratorRecordsResponse,
  type OrchestratorPause,
  type OrchestratorRunResponse,
  type OrchestratorRunsResponse,
  type OutcomeRule,
  type PreviewResponse,
  type QueueGroup,
  type QueueOrderBy,
  type QueueOrderDir,
  type RoutesResolutionResponse,
  type SuppressionsResponse,
} from "./api";

export const keys = {
  me: ["me"] as const,
  accounts: ["accounts"] as const,
  account: (id: string) => ["account", id] as const,
  callbacks: (id: string, filters: unknown) => ["callbacks", id, filters] as const,
  callback: (accountId: string, id: string) => ["callback", accountId, id] as const,
  agents: (id: string) => ["eightloop-agents", id] as const,
  /** The account's callback list. Prefixed so one write refreshes the tab and every agent page. */
  callbackAgents: (id: string) => ["callback-agents", id] as const,
  agentFields: (id: string) => ["agent-fields", id] as const,
  routeResolution: (id: string) => ["route-resolution", id] as const,
  codeActionPlan: (id: string, agentId: string, channelId: string) =>
    ["code-action-plan", id, agentId, channelId] as const,
  // Paged keys carry their page in the key, so every page is cached separately and going
  // back is instant. The prefix is what invalidation targets — `["suppressions", id]`
  // clears every page of that account's list at once, which is what adding or removing a
  // number has to do: the row it changed could be on any of them.
  suppressionsFor: (id: string) => ["suppressions", id] as const,
  suppressions: (id: string, filters: unknown) => ["suppressions", id, filters] as const,
  orchestrator: ["orchestrator"] as const,
  orchestratorForAccount: (accountId: string) => ["orchestrator", "account", accountId] as const,
  orchestratorCustomer: (customer: string) => ["orchestrator", customer] as const,
  orchestratorRecords: (customer: string, filters: unknown) =>
    ["orchestrator", customer, "records", filters] as const,
  orchestratorRuns: (customer: string, filters: unknown) =>
    ["orchestrator", customer, "runs", filters] as const,
};

export function useMe(): UseQueryResult<MeResponse> {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api<MeResponse>("/me"),
    // `/me` answers 200 with `user: null` when signed out, so a failure here is a
    // real failure and worth retrying once.
    retry: 1,
    staleTime: 30_000,
  });
}

export function useAccounts(enabled = true) {
  return useQuery({
    queryKey: keys.accounts,
    queryFn: () => api<{ accounts: AccountWithQueue[] }>("/accounts"),
    enabled,
  });
}

export function useAccountConfig(accountId: string | undefined) {
  return useQuery({
    queryKey: keys.account(accountId ?? ""),
    queryFn: () => api<AccountConfig>(`/accounts/${accountId}`),
    enabled: Boolean(accountId),
  });
}

export type QueueFilters = {
  group?: QueueGroup | null;
  agent_id?: string | null;
  channel_id?: string | null;
  outcome?: string | null;
  q?: string | null;
  /** ISO instants. `to` is left null by the rolling presets so upcoming calls stay visible. */
  from?: string | null;
  to?: string | null;
  /** Omit both to take the server's per-tab default; see `defaultOrderDir`. */
  order_by?: QueueOrderBy | null;
  order_dir?: QueueOrderDir | null;
  limit: number;
  offset: number;
};

export function useCallbacks(accountId: string | undefined, filters: QueueFilters) {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== null && value !== undefined && value !== "") search.set(key, String(value));
  }
  return useQuery({
    queryKey: keys.callbacks(accountId ?? "", filters),
    queryFn: () => api<CallbackListResponse>(`/accounts/${accountId}/callbacks?${search}`),
    enabled: Boolean(accountId),
    // Keeps the previous page on screen while the next one loads, so the table
    // does not collapse to a skeleton on every filter change.
    placeholderData: (prev) => prev,
  });
}

export function useCallback_(accountId: string | undefined, callbackId: string | undefined) {
  return useQuery({
    queryKey: keys.callback(accountId ?? "", callbackId ?? ""),
    queryFn: () => api<CallbackDetail>(`/accounts/${accountId}/callbacks/${callbackId}`),
    enabled: Boolean(accountId && callbackId),
  });
}

/**
 * Agents and channels read live from the tenant. Fails loudly if credentials are wrong.
 *
 * `retry: false` on purpose: the usual failure here is 401 from a revoked key, and
 * retrying that three times only delays the message that says so.
 */
export function useEightloopAgents(accountId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.agents(accountId ?? ""),
    queryFn: () => api<EightloopAgentsResponse>(`/accounts/${accountId}/eightloop/agents`),
    enabled: Boolean(accountId) && enabled,
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/**
 * Every agent's conversation fields, from this app's cache.
 *
 * Cached rather than live for the reason the channel list is: this powers the field
 * pickers on Context and on the test call, and those screens have to render when the
 * credential is broken — which is exactly when someone is looking at them. It is also
 * one request for the whole account instead of one per agent in a dropdown.
 */
export function useAgentFields(accountId: string | undefined) {
  return useQuery({
    queryKey: keys.agentFields(accountId ?? ""),
    queryFn: () => api<AgentFieldsResponse>(`/accounts/${accountId}/eightloop/agent-fields`),
    enabled: Boolean(accountId),
    staleTime: 5 * 60_000,
  });
}

/**
 * "So where will each agent's callbacks actually go?"
 *
 * Answered by the server running the real resolver. Kept out of `useAccountConfig`
 * because it makes four live 8loop reads and the rest of the config page must not
 * wait on the tenant to render.
 */
export function useRouteResolution(accountId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: keys.routeResolution(accountId ?? ""),
    queryFn: () => api<RoutesResolutionResponse>(`/accounts/${accountId}/routes/resolution`),
    enabled: Boolean(accountId) && enabled,
    retry: false,
    staleTime: 60_000,
  });
}

/**
 * What the app would install as one agent's post-call webhook, and why.
 *
 * Read-only and safe to render on sight: it makes several live 8loop reads (the agent's
 * conversation fields, its versions, its active webhooks) and writes nothing. No token
 * goes near it — this is a GET, so anything it carried would land in an access log, and
 * the script it returns therefore holds a placeholder rather than a live credential.
 *
 * `staleTime: 0` on purpose, against the convention of the other 8loop queries here.
 * Everything this answers can change under the FDE from the 8loop UI in another tab —
 * somebody opens a draft, adds the field you were about to create — and a five-minute
 * cache would let them press Install against a plan that is no longer true.
 */
export function useCodeActionPlan(
  accountId: string | undefined,
  agentId: string | null,
  channelId?: string | null,
) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: keys.codeActionPlan(accountId ?? "", agentId ?? "", channelId ?? ""),
    queryFn: () =>
      api<CodeActionPlanResponse>(
        `/accounts/${accountId}/code-action?agent_id=${encodeURIComponent(agentId ?? "")}` +
          (channelId ? `&channel_id=${encodeURIComponent(channelId)}` : ""),
      ),
    enabled: Boolean(accountId) && Boolean(agentId),
    retry: false,
    staleTime: 0,
  });

  /**
   * The plan endpoint writes what it just saw back onto the enrollment row, so every cached copy
   * of that row is stale the moment a plan lands. Refreshing the list is what keeps the rest of
   * the page telling the same story: without it, the remove dialog reads the row from before the
   * check and can promise "the hook stays installed and keeps posting" a click before the toast
   * reports, correctly, that nothing was reaching this app.
   */
  useEffect(() => {
    if (!query.data || !accountId) return;
    void qc.invalidateQueries({ queryKey: keys.callbackAgents(accountId) });
  }, [query.data, accountId, qc]);

  return query;
}

/**
 * The account's callback list: the agents on it, the ones the Add dialog can offer, and the
 * ones already sending callbacks that nobody added.
 *
 * One query serves both the Agents tab and each agent's own page — the page picks its row out
 * of the list rather than fetching itself, so a save on the page updates the tab behind it and
 * there is only ever one copy of an agent's status in the cache.
 *
 * Nothing here touches 8loop, so unlike the live queries it is safe to keep briefly and safe
 * to retry.
 */
export function useCallbackAgents(accountId: string | undefined) {
  return useQuery({
    queryKey: keys.callbackAgents(accountId ?? ""),
    queryFn: () => api<CallbackAgentsResponse>(`/accounts/${accountId}/agents`),
    enabled: Boolean(accountId),
    staleTime: 10_000,
  });
}

/**
 * A page of the do-not-call list, optionally narrowed by a typed number.
 *
 * `q` is sent as typed and stripped to digits server-side, so "+91 95685 07028" finds the
 * row stored as `+919568507028`. It searches the whole table rather than the current page —
 * that is the entire reason it exists, since the question people bring here is "is this
 * number on the list?" and scrolling is not an answer once there are thousands of rows.
 *
 * `placeholderData` keeps the previous page visible while the next one loads, so paging does
 * not blink the table out to skeletons on every arrow press.
 */
export function useSuppressions(
  accountId: string | undefined,
  opts: { limit?: number; offset?: number; q?: string } = {},
) {
  const limit = opts.limit ?? 50;
  const offset = opts.offset ?? 0;
  const q = opts.q?.trim() ?? "";
  const search = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (q !== "") search.set("q", q);
  return useQuery({
    queryKey: keys.suppressions(accountId ?? "", { limit, offset, q }),
    queryFn: () =>
      api<SuppressionsResponse>(`/accounts/${accountId}/suppressions?${search.toString()}`),
    enabled: Boolean(accountId),
    placeholderData: (prev) => prev,
  });
}

// -------------------------------------------------------------- mutations ----

/**
 * Every config mutation invalidates the whole account config rather than patching
 * the cache by hand. The response bodies are small and the alternative is a set of
 * merge rules that drift from the server's — a saved calling window that appears
 * in the UI but not in the database is the worst possible bug on this screen.
 */
function useAccountMutation<TArgs, TResult>(
  accountId: string | undefined,
  fn: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.account(accountId ?? "") });
      void qc.invalidateQueries({ queryKey: keys.accounts });
      // Editing a route changes where calls go, and that answer is computed
      // server-side — leaving it cached would show the old destination next to the
      // new rule, which is worse than showing nothing.
      void qc.invalidateQueries({ queryKey: keys.routeResolution(accountId ?? "") });
      // And the agents list, which is built from the channel cache: every agent row's
      // channel count, "none of them can dial" and "missing from the tenant" come from it.
      // Refreshing channels and watching the agent still say it has no channels — the very
      // thing you pressed Refresh to fix — is how that list loses the reader's trust.
      void qc.invalidateQueries({ queryKey: keys.callbackAgents(accountId ?? "") });
      void qc.invalidateQueries({ queryKey: keys.agentFields(accountId ?? "") });
    },
  });
}

export function useUpdateAccount(accountId: string | undefined) {
  return useAccountMutation(accountId, (body: Record<string, unknown>) =>
    patch(`/accounts/${accountId}`, body),
  );
}

export type CreateAccountInput = {
  name: string;
  slug: string;
  tenant_host: string;
  base_url: string;
  timezone: string;
  default_country_code: string | null;
  api_key: string;
};

export type CreateAccountResponse = {
  account: Account;
  /** Plaintext, returned once and never again — only its hash is stored. */
  ingest_token: string;
  ingest_url: string;
  message: string;
};

/**
 * Admin-only server-side. Invalidates the account list *and* `/me`, because `/me`
 * carries its own copy of the accounts the session can see — without that the new
 * account is in the table but the rail cannot resolve it.
 */
export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateAccountInput) => post<CreateAccountResponse>("/accounts", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.accounts });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useSaveWindows(accountId: string | undefined) {
  return useAccountMutation(accountId, (windows: CallingWindow[]) =>
    put<{ calling_windows: CallingWindow[]; message: string }>(
      `/accounts/${accountId}/calling-windows`,
      { windows },
    ),
  );
}

/**
 * An override, keyed on the channel it overrides.
 *
 * One call does both create and edit, because there is exactly one override per
 * channel: "where do callbacks from 287 go" has a single answer, and a create/update
 * pair would only introduce a way to get two.
 */
export type OverrideInput = Omit<ChannelRoute, "id" | "account_id">;

export function useSaveOverride(accountId: string | undefined) {
  return useAccountMutation(accountId, (body: OverrideInput) =>
    put(`/accounts/${accountId}/routes/${encodeURIComponent(body.source_channel_id)}`, body),
  );
}

export function useDeleteOverride(accountId: string | undefined) {
  return useAccountMutation(accountId, (channelId: string) =>
    del(`/accounts/${accountId}/routes/${encodeURIComponent(channelId)}`),
  );
}

/**
 * Ask 8loop for the channel list again.
 *
 * Arun: "is there a sync to get latest or something?" — there is no push from 8loop and
 * no webhook for channel changes, so this button is the sync. Everything that reads the
 * cache shows its age next to it rather than implying the data is live.
 */
export function useRefreshChannels(accountId: string | undefined) {
  return useAccountMutation(accountId, () =>
    post<{ message: string; checked_phone_numbers: boolean }>(
      `/accounts/${accountId}/eightloop/channels/refresh`,
      {},
    ),
  );
}

export function useSaveOutcomeRules(accountId: string | undefined) {
  return useAccountMutation(accountId, (rules: Partial<OutcomeRule>[]) =>
    put(`/accounts/${accountId}/outcome-rules`, { rules }),
  );
}

/*
  There is no `useSaveFieldMapping` any more, and the endpoint it called is deliberately left
  standing.

  Explicit source → target mappings are not how context travels: `callback_context` and the
  `previous_<field>` names are sent to any agent that declares them, decided per agent in block 2
  of its hook setup. So nothing in the UI creates a mapping row, and the account-wide card that
  used to (context-section.tsx, deleted 2026-09-04) is gone. Existing rows are real work and are
  still honoured — the delete below is what the agent's page uses to stop one.
*/
export function useDeleteFieldMapping(accountId: string | undefined) {
  return useAccountMutation(accountId, (id: string) =>
    del(`/accounts/${accountId}/field-mappings/${id}`),
  );
}

export function useRotateIngestToken(accountId: string | undefined) {
  return useAccountMutation(accountId, () =>
    post<{ ingest_token: string; message: string }>(`/accounts/${accountId}/ingest-token`),
  );
}

/**
 * The verdict is written to the account row server-side, so this refetches on
 * `onSettled` rather than `onSuccess` — a *failed* check is exactly the case where
 * the page has new information to show.
 *
 * Observed before this existed: pressing Test connection on an account 8loop was
 * 401-ing recorded the rejection in the database and left the setup checklist still
 * reading "nobody has ever asked 8loop whether it is accepted", because nothing
 * invalidated the config query. The toast said one thing and the checklist another.
 */
export function useCheckConnection(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      post<{ ok: boolean; message: string; details?: Record<string, unknown> }>(
        `/accounts/${accountId}/eightloop/check`,
      ),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.account(accountId ?? "") });
      // The accounts table shows the same verdict per row.
      void qc.invalidateQueries({ queryKey: keys.accounts });
    },
  });
}

export function usePreviewIngest(accountId: string | undefined) {
  return useMutation({
    mutationFn: (payload: unknown) =>
      post<PreviewResponse>(`/accounts/${accountId}/ingest/preview`, payload),
  });
}

/**
 * The three things the code-action screen can do, all POSTs.
 *
 * POST rather than GET for the same reason throughout: every one of them either carries
 * a live ingest token in the request or hands one back in the response, and a GET would
 * put that credential in the browser's history and the server's access log.
 *
 * None of them invalidate the account config — they change the tenant's agent in 8loop,
 * not anything this app stores. `install` does invalidate the plan, because the plan
 * describes exactly what it just changed.
 */
export type CodeActionTarget = {
  agent_id: string;
  channel_id?: string;
  ingest_token?: string;
  rotate_token?: boolean;
  preview?: boolean;
  assume_created?: ("callback_time" | "disposition")[];
  /**
   * The ticks from the agent's page. They shape the brief and nothing else — which is what
   * lets that screen promise that ticking a box changes nothing until somebody runs it.
   */
  context_for?: string[];
  create_context?: string[];
  merge_wording?: boolean;
  want_context?: boolean;
};

export function useCodeActionScript(accountId: string | undefined) {
  return useMutation({
    mutationFn: (body: CodeActionTarget) =>
      post<CodeActionScriptResponse>(`/accounts/${accountId}/code-action/script`, body),
  });
}

export function useCodeActionInstall(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      body: CodeActionTarget & {
        create_fields?: ("callback_time" | "disposition")[];
        use_existing_draft?: boolean;
        publish?: boolean;
        dry_run?: boolean;
      },
    ) => post<CodeActionInstallResponse>(`/accounts/${accountId}/code-action/install`, body),
    // Even a dry run is worth refetching after: it reads the agent live, and if it found
    // a draft that was not there a minute ago the plan on screen is already stale.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["code-action-plan", accountId ?? ""] });
      // The server records what it verified onto the agent's row, so the Agents list behind
      // this page is out of date the moment an install succeeds.
      void qc.invalidateQueries({ queryKey: keys.callbackAgents(accountId ?? "") });
    },
  });
}

/**
 * Publishing the agent, which is what makes a staged hook fire.
 *
 * Its own mutation because it is its own decision — installing writes to the draft and reaches
 * no caller; this ships everything in that draft. `confirm` is sent per request and never
 * remembered, so the guard cannot be satisfied by something the reader agreed to earlier.
 */
export function useCodeActionPublish(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { agent_id: string; confirm?: boolean; dry_run?: boolean }) =>
      post<CodeActionInstallResponse>(`/accounts/${accountId}/code-action/publish`, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["code-action-plan", accountId ?? ""] });
      void qc.invalidateQueries({ queryKey: keys.callbackAgents(accountId ?? "") });
    },
  });
}

/**
 * Recover the account's ingest token from a hook already installed on the tenant.
 *
 * For an account whose token predates the app storing its own: the plaintext is in the
 * installed webhook's code, so it can be read back and kept without rotating anything.
 */
export function useAdoptIngestToken(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { agent_id: string }) =>
      post<{ recovered_from: string; message: string }>(
        `/accounts/${accountId}/code-action/adopt-token`,
        body,
      ),
    onSuccess: () => {
      // The account's credential summary is what every "paste a token" control keys off.
      void qc.invalidateQueries({ queryKey: keys.account(accountId ?? "") });
      void qc.invalidateQueries({ queryKey: ["code-action-plan", accountId ?? ""] });
    },
  });
}

/**
 * Which of an agent's fields plays a role — the callback time, the disposition.
 *
 * The app guesses, well; this is how it gets overruled on an agent with two plausible fields.
 * Persisted, because a picker that forgets on reload would mean the brief is generated from a
 * field nobody chose.
 */
export function useSetFieldRole(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      agent_id: string;
      role: "callback_time" | "disposition";
      field_name: string | null;
    }) => put<{ message: string }>(`/accounts/${accountId}/code-action/field-role`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["code-action-plan", accountId ?? ""] });
    },
  });
}

/**
 * What one of this agent's own disposition values means.
 *
 * The app guesses with an ordered set of regexes; this is how a wrong guess gets corrected and
 * how a value it could not place at all gets an answer. Saved immediately, because a select
 * with a save button beside it is two decisions where there is one.
 */
export function useSetDispositionValue(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      agent_id: string;
      field_name: string;
      value: string;
      outcome?: string | null;
      clear?: boolean;
    }) => put<{ message: string }>(`/accounts/${accountId}/code-action/disposition-value`, body),
    onSuccess: () => {
      // The plan carries the map, so it is stale the moment a meaning changes.
      void qc.invalidateQueries({ queryKey: ["code-action-plan", accountId ?? ""] });
    },
  });
}

/**
 * This agent's last few finished calls, for testing the hook against one that really happened.
 *
 * Enabled only when somebody opens the test card: it is a live 8loop read, and paying for it on
 * every page load to fill a picker nobody has looked at is the kind of cost that makes a page
 * feel slow for no reason.
 */
export function useRecentCalls(
  accountId: string | undefined,
  agentId: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["code-action-recent-calls", accountId ?? "", agentId ?? ""],
    queryFn: () =>
      api<{ calls: CodeActionRecentCall[] }>(
        `/accounts/${accountId}/code-action/recent-calls?agent_id=${encodeURIComponent(agentId ?? "")}`,
      ),
    enabled: enabled && Boolean(accountId) && Boolean(agentId),
    retry: false,
    staleTime: 60_000,
  });
}

export function useCodeActionTest(accountId: string | undefined) {
  return useMutation({
    mutationFn: (
      body: CodeActionTarget & { call_id?: string; field_values?: Record<string, string> },
    ) => post<CodeActionTestResponse>(`/accounts/${accountId}/code-action/test`, body),
  });
}

// ------------------------------------------------------------- callback agents ----

/**
 * Add, edit and remove agents on the account's callback list.
 *
 * These invalidate the list *and* the account config, because the setup checklist on the same
 * screen counts agents: adding the first one is what turns "no agent is set up for callbacks"
 * green, and a checklist that still says otherwise after a successful add would be read as the
 * add having failed.
 */
function useCallbackAgentMutation<TArgs, TResult>(
  accountId: string | undefined,
  fn: (args: TArgs) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.callbackAgents(accountId ?? "") });
      void qc.invalidateQueries({ queryKey: keys.account(accountId ?? "") });
    },
  });
}

export function useAddCallbackAgent(accountId: string | undefined) {
  return useCallbackAgentMutation(
    accountId,
    (body: { agent_id: string; agent_name?: string | null; note?: string | null }) =>
      post<{ agent: CallbackAgent; message: string }>(`/accounts/${accountId}/agents`, body),
  );
}

export function usePatchCallbackAgent(accountId: string | undefined) {
  return useCallbackAgentMutation(
    accountId,
    ({
      agentId,
      ...body
    }: {
      agentId: string;
      note?: string | null;
    }) =>
      patch<{ agent: CallbackAgent; message: string }>(
        `/accounts/${accountId}/agents/${encodeURIComponent(agentId)}`,
        body,
      ),
  );
}

export function useRemoveCallbackAgent(accountId: string | undefined) {
  return useCallbackAgentMutation(accountId, (agentId: string) =>
    del<{ removed: boolean; message: string }>(
      `/accounts/${accountId}/agents/${encodeURIComponent(agentId)}`,
    ),
  );
}

export type CallbackAction =
  | { action: "dial_now" }
  | { action: "reschedule"; at: string }
  | { action: "cancel"; note?: string }
  | { action: "recheck" };

export function useCallbackAction(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ callbackId, ...body }: CallbackAction & { callbackId: string }) =>
      post<{ message: string }>(`/accounts/${accountId}/callbacks/${callbackId}/actions`, body),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: keys.callback(accountId ?? "", variables.callbackId) });
      void qc.invalidateQueries({ queryKey: ["callbacks", accountId ?? ""] });
      void qc.invalidateQueries({ queryKey: keys.accounts });
    },
  });
}

export function useAddSuppression(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { phone_number: string; reason?: string; note?: string | null }) =>
      post(`/accounts/${accountId}/suppressions`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.suppressionsFor(accountId ?? "") });
      void qc.invalidateQueries({ queryKey: ["callbacks", accountId ?? ""] });
    },
  });
}

export function useRemoveSuppression(accountId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phone: string) =>
      del(`/accounts/${accountId}/suppressions/${encodeURIComponent(phone)}`),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: keys.suppressionsFor(accountId ?? "") }),
  });
}

// ------------------------------------------------------ the CRM orchestrator ---
//
// Not account-scoped, unlike everything above: a CRM customer is a config entry that
// happens to dial *through* an account, so these hang off `/api/orchestrator` and take a
// customer key rather than an account id. That is the M3 separation — Arun: "please keep
// this separate from call back list since the jobs here are different" — carried through to
// the query keys, so nothing here is invalidated by an account edit and vice versa.
//
// Still nothing on a timer, per the note at the top of this file. A run that took ninety
// seconds and dialled a hundred people is not something to discover from a table that
// silently redrew; the page has a refresh control and every action invalidates.

export function useOrchestrator() {
  return useQuery({
    queryKey: keys.orchestrator,
    queryFn: () => api<OrchestratorListResponse>("/orchestrator"),
    retry: false,
  });
}

/**
 * The CRM feeds belonging to one account. An account may have several, or none.
 *
 * Filtered server-side rather than client-side because the config keys a feed's account by
 * slug: matching that against an account id in the browser means either fetching every account
 * to build a slug map, or guessing.
 */
export function useOrchestratorForAccount(accountId: string | undefined) {
  return useQuery({
    queryKey: keys.orchestratorForAccount(accountId ?? ""),
    queryFn: () =>
      api<OrchestratorListResponse>(`/orchestrator?account_id=${encodeURIComponent(accountId!)}`),
    enabled: Boolean(accountId),
    retry: false,
  });
}

export function useOrchestratorCustomer(customer: string | undefined) {
  return useQuery({
    queryKey: keys.orchestratorCustomer(customer ?? ""),
    queryFn: () => api<OrchestratorDetailResponse>(`/orchestrator/${customer}`),
    enabled: Boolean(customer),
    retry: false,
  });
}

/**
 * A page of stored records.
 *
 * Paged rather than "the last 200" because this table is the one that grows without bound:
 * Dr Agarwals stores over a thousand records a day, so a fixed cap would be showing four
 * hours of a list somebody opened to look through a month of. Arun: "this will be a big
 * list after a few days … otherwise this page will not load well."
 */
export function useOrchestratorRecords(
  customer: string | undefined,
  status: OrchestratorRecordStatus | "all",
  opts: { limit?: number; offset?: number } = {},
) {
  const limit = opts.limit ?? 25;
  const offset = opts.offset ?? 0;
  const search = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (status !== "all") search.set("status", status);
  return useQuery({
    queryKey: keys.orchestratorRecords(customer ?? "", { status, limit, offset }),
    queryFn: () =>
      api<OrchestratorRecordsResponse>(`/orchestrator/${customer}/records?${search.toString()}`),
    enabled: Boolean(customer),
    retry: false,
    placeholderData: (prev) => prev,
  });
}

/**
 * A page of run history.
 *
 * Its own request rather than a field on the feed response: a fifteen-minute poll over a
 * nine-hour window is 36 runs a day, each carrying a jsonb blob of counters, and opening the
 * page should not download a week of them to render a header.
 */
export function useOrchestratorRuns(
  customer: string | undefined,
  opts: { limit?: number; offset?: number; enabled?: boolean } = {},
) {
  const limit = opts.limit ?? 20;
  const offset = opts.offset ?? 0;
  return useQuery({
    queryKey: keys.orchestratorRuns(customer ?? "", { limit, offset }),
    queryFn: () =>
      api<OrchestratorRunsResponse>(
        `/orchestrator/${customer}/runs?limit=${limit}&offset=${offset}`,
      ),
    enabled: Boolean(customer) && opts.enabled !== false,
    retry: false,
    placeholderData: (prev) => prev,
  });
}

/**
 * Run one customer's pipeline now.
 *
 * `dry_run` is not defaulted here on purpose. The server defaults it to `true`, which is the
 * right default for a stray curl, but a UI that relied on that default would have one button
 * whose behaviour depended on a value it never sent — so both buttons state their intent.
 *
 * Invalidates records and the customer, because a real run writes rows and a run of any kind
 * writes a `crm_runs` entry the run list is showing.
 */
export function useOrchestratorRun(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { dry_run: boolean; preset: OrchestratorPreset; now?: string }) =>
      post<OrchestratorRunResponse>(`/orchestrator/${customer}/run`, body),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.orchestrator });
    },
  });
}

/**
 * Stop this feed, and start it again.
 *
 * Arun: *"i need a way to enable or disable the crm … how can i stop these runs when i want and
 * reusme when i want?"* Deliberately separate from clearing a halt: a halt is the feed stopping
 * itself over something broken, a pause is a person choosing to stop it.
 *
 * Invalidates the whole orchestrator prefix, because the feeds table, the feed header and the
 * run buttons all read the pause.
 */
export function useOrchestratorPause(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { note?: string }) =>
      put<{ pause: OrchestratorPause | null; message: string }>(
        `/orchestrator/${customer}/pause`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.orchestrator });
    },
  });
}

/**
 * The channels a CRM feed could dial on.
 *
 * Enabled only when the picker is open — the cheap read is the cache, but this is still a
 * request nobody needs before they reach for it. `refresh` is the "get latest channels" button
 * and goes to 8loop.
 */
export function useOrchestratorChannels(customer: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...keys.orchestrator, customer, "channels"] as const,
    enabled: Boolean(customer) && enabled,
    queryFn: () => api<OrchestratorChannelsResponse>(`/orchestrator/${customer}/channels`),
  });
}

export function useOrchestratorRefreshChannels(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<OrchestratorChannelsResponse>(`/orchestrator/${customer}/channels?refresh=1`),
    onSuccess: (data) => {
      qc.setQueryData([...keys.orchestrator, customer, "channels"], data);
    },
  });
}

export function useOrchestratorSetChannel(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { routing_identifier?: string; clear?: boolean }) =>
      put<{ chosen: OrchestratorChannelsResponse["chosen"]; message: string }>(
        `/orchestrator/${customer}/channel`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.orchestrator });
    },
  });
}

export function useOrchestratorResume(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      del<{ resumed: boolean; message: string }>(`/orchestrator/${customer}/pause`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.orchestrator });
    },
  });
}

/**
 * Store one or more of a feed's CRM credentials.
 *
 * A record rather than one field per call, because both LeadSquared keys are entered together
 * and saving them one at a time would leave a window where the feed has half a login. Blank
 * fields are filtered out here as well as refused by the server: the form renders every slot,
 * and "I only replaced the secret key" must not wipe the access key.
 *
 * Invalidates the whole orchestrator prefix — the feeds table shows "credentials missing" and
 * the run buttons are disabled on `ready`, so both stale after this.
 */
export function useOrchestratorSaveCredentials(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (values: Record<string, string>) =>
      put<OrchestratorCredentialsResponse>(`/orchestrator/${customer}/credentials`, { values }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.orchestrator });
    },
  });
}

/** Forget one. The feed stops reading the CRM, which is sometimes exactly the point. */
export function useOrchestratorClearCredential(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slot: string) =>
      del<OrchestratorCredentialsResponse>(
        `/orchestrator/${customer}/credentials/${encodeURIComponent(slot)}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.orchestrator });
    },
  });
}

/** Clear the halt latch. Deliberately explicit — a latch that clears itself is just a loop. */
export function useOrchestratorClearHalt(customer: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      del<{ cleared: boolean; message: string }>(`/orchestrator/${customer}/halt`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.orchestrator });
    },
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => post("/login", body),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post("/logout"),
    onSuccess: () => {
      qc.clear();
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}
